import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollapseBlock } from '../../../../../common';
import FormTextField from '../../../../../antd/form/FormTextField/FormTextField';
import { required } from '../../../../../antd/form/FormValidation';
import PropTypes from 'prop-types';
import FormTextArea from '../../../../../antd/form/FormTextArea/FormTextArea';
import { Col, InputNumber, Row } from 'antd';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect';
import FormItem from 'antd/es/form/FormItem';
import FormDate from '../../../../../antd/form/FormDate/FormDate';
import FormButton from '../../../../../antd/form/FormButton/FormButton';
import FormButtonsContainer from '../../../../../antd/form/FormButtonsContainer/FormButtonsContainer';
import FormTextEditor from '../../../../../antd/form/FormTextEditor/FormTextEditor';
import { Form as AntdForm } from 'antd';
import Form from '../../../../../antd/form/Form/Form';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import FormSwitch from '../../../../../antd/form/FormSwitch/FormSwitch.jsx';
import { allAllowedTypes } from '../../../../../../hooks/useFileDraggerFileValidation.jsx';

dayjs.extend(isSameOrBefore);

const FijJobForm = ({
  areaOfUses,
  studyPrograms,
  teachers,
  initialValues,
  onSubmit,
  archiveMode,
  isFijAdmin,
}) => {
  const { t } = useTranslation();
  const [form] = AntdForm.useForm();
  const { getFieldValue } = form;

  const defaultGrantText = t(
    'v rozmedzí od 100 € do 500 € v závislosti od množstva vykonanej práce',
  );

  const [selectedStudyPrograms, setSelectedStudyPrograms] = useState(
    initialValues?.study_programs || [],
  );

  // Derive selected study program objects from initial values and available studyPrograms
  useEffect(() => {
    const ids = Array.isArray(initialValues?.study_programs)
      ? initialValues.study_programs.map((sp) =>
          typeof sp === 'object' ? sp.id : sp,
        )
      : [];
    if (Array.isArray(studyPrograms) && ids.length > 0) {
      const selected = studyPrograms.filter((sp) => ids.includes(sp.id));
      setSelectedStudyPrograms(selected);
    }
  }, [initialValues?.study_programs, studyPrograms]);

  useEffect(() => {}, [selectedStudyPrograms]);

  const areaOfUsesOptions = useMemo(
    () =>
      areaOfUses?.map((item) => ({
        value: item.id,
        label: item.area,
      })),
    [areaOfUses],
  );

  const studyProgramsOptions = useMemo(
    () =>
      studyPrograms?.map((item) => ({
        value: item.id,
        label: item.name + ' (' + item.study_program_type.shortcut + ')',
      })),
    [studyPrograms],
  );

  const teachersOptions = useMemo(
    () =>
      teachers?.map((item) => ({
        value: String(item.id ?? item.person_id),
        label: item.full_name,
      })),
    [teachers],
  );

  const handleStudyProgramsChange = (selectedPrograms) => {
    const selectedStudyPrograms = studyPrograms.filter((value) =>
      selectedPrograms.includes(value.id),
    );
    setSelectedStudyPrograms(selectedStudyPrograms);
  };

  const watchFrom = AntdForm.useWatch('date_from', form);

  const disabledDate = (current) => {
    return current.isSameOrBefore(dayjs(), 'day'); // Disable all dates before today
  };

  const disabledDateTo = (current) => {
    if (!watchFrom) {
      return current.isBefore(dayjs(), 'day');
    }
    return watchFrom.isAfter(current, 'day'); // Disable all dates before day of 'from' date
  };

  // Initialize form values (do NOT overwrite user-entered values on re-renders)
  useEffect(() => {
    if (!initialValues) return;

    const currentFrom = form.getFieldValue('date_from');
    const currentTo = form.getFieldValue('date_to');
    const currentStudyPrograms = form.getFieldValue('study_programs');
    const currentSupervisor = form.getFieldValue('supervisor_id');
    const currentGrant = form.getFieldValue('grant');

    const patch = {};

    if (archiveMode) {
      if (currentFrom === undefined) patch.date_from = undefined;
      if (currentTo === undefined) patch.date_to = undefined;
    } else {
      if (currentFrom === undefined) {
        patch.date_from = initialValues.date_from
          ? dayjs(initialValues.date_from, 'YYYY-MM-DD')
          : undefined;
      }
      if (currentTo === undefined) {
        patch.date_to = initialValues.date_to
          ? dayjs(initialValues.date_to, 'YYYY-MM-DD')
          : undefined;
      }
    }

    // Map study_programs to array of IDs (accepts [{id}] or [id])
    if (
      !Array.isArray(currentStudyPrograms) ||
      currentStudyPrograms.length === 0
    ) {
      patch.study_programs = Array.isArray(initialValues.study_programs)
        ? initialValues.study_programs.map((sp) =>
            typeof sp === 'object' ? sp.id : sp,
          )
        : [];
    }

    // Ensure supervisor_id is prefilled on create when provided
    if (currentSupervisor === undefined && initialValues.supervisor_id) {
      patch.supervisor_id = String(initialValues.supervisor_id);
    }

    // Prefill remaining simple fields if not set yet
    const simpleFields = [
      'name',
      'content',
      'result',
      'student_capacity',
      'grant',
      'fij_area_of_use_id',
      'it_skills',
      'language_knowledge',
      'other_text',
    ];

    simpleFields.forEach((field) => {
      if (
        form.getFieldValue(field) === undefined &&
        initialValues[field] !== undefined
      ) {
        patch[field] = initialValues[field];
      }
    });

    if (
      (currentGrant === undefined ||
        currentGrant === null ||
        String(currentGrant).trim() === '') &&
      (patch.grant === undefined ||
        patch.grant === null ||
        String(patch.grant).trim() === '')
    ) {
      patch.grant = defaultGrantText;
    }

    // Teachers: expects array of IDs
    if (
      form.getFieldValue('teachers') === undefined &&
      Array.isArray(initialValues.teachers)
    ) {
      patch.teachers = initialValues.teachers.map((t) =>
        String(typeof t === 'object' ? t.id : t),
      );
    }

    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch);
    }
  }, [
    initialValues,
    form,
    archiveMode,
    selectedStudyPrograms,
    defaultGrantText,
  ]);

  useEffect(() => {
    if (initialValues?.supervisor_id && !form.getFieldValue('supervisor_id')) {
      form.setFieldValue('supervisor_id', String(initialValues.supervisor_id));
    }
  }, [initialValues?.supervisor_id, form]);

  return (
    <>
      <Form form={form} onFinish={onSubmit} layout="vertical">
        <CollapseBlock title={t('Základné informácie')} collapsible={true}>
          <FormTextField
            label={t('Názov')}
            name="name"
            rules={[required(t)]}
            extra={t('Názov FRI praxe musí byť unikátny, nesmie byť rovnaký ako má iná FRI prax.')}
          />

          <FormSelect
            label={t('Lektor')}
            name={'supervisor_id'}
            options={teachersOptions}
            placeholder={t('Vyhľadať lektora')}
            rules={[required(t)]}
          />

          <FormSelect
            label={t('Ďalší lektori')}
            name={'teachers'}
            options={teachersOptions}
            placeholder={t('Vyhľadať lektora')}
            mode={'multiple'}
          />

          <FormItem label={t('Realizácia')} rules={[required(t)]}>
            <Row gutter={[10]}>
              <Col span={5}>
                <FormDate
                  name={'date_from'}
                  label={t('Od')}
                  rules={[required(t)]}
                  style={{ width: '100%' }}
                  disabledDate={disabledDate}
                />
              </Col>
              <Col span={5}>
                <FormDate
                  name={'date_to'}
                  label={t('Do')}
                  rules={[
                    {
                      validator: async (_, to) => {
                        let from = getFieldValue('date_from');
                        if (
                          from != null &&
                          to != null &&
                          dayjs(to).isBefore(from, 'day')
                        ) {
                          return Promise.reject(
                            t('Koniec musí byť neskôr ako začiatok'),
                          );
                        }
                      },
                    },
                    required(t),
                  ]}
                  disabledDate={disabledDateTo}
                  style={{ width: '100%' }}
                />
              </Col>
            </Row>
          </FormItem>

          <FormTextEditor
            label={t('Náplň FRI praxe')}
            name="content"
            imgUploadDirectory={'fri_internal_jobs'}
            fileUploadDirectory={'fri_internal_jobs'}
            allowedTypes={allAllowedTypes}
            enableCustomFileName={true}
            rules={[required(t)]}
          />

          <FormItem
            label={t('Max. počet študentov')}
            name="student_capacity"
            rules={[required(t)]}
          >
            <InputNumber name="student_capacity" type={'number'} min={1} />
          </FormItem>
        </CollapseBlock>
        <CollapseBlock title={t('Rozšírené informácie')} collapsible={true}>
          <FormTextEditor
            label={t('Očakávaný výsledok')}
            name="result"
            imgUploadDirectory={'fri_internal_jobs'}
            fileUploadDirectory={'fri_internal_jobs'}
            allowedTypes={allAllowedTypes}
            enableCustomFileName={true}
            rules={[required(t)]}
          />

          <FormTextField
            label={t('Výška navrhovaného štipendia')}
            name="grant"
          />

          <FormSelect
            label={t('Oblasť využitia')}
            name="fij_area_of_use_id"
            placeholder={t('Vyhľadať oblasť využitia')}
            options={areaOfUsesOptions}
            rules={[required(t)]}
          />
        </CollapseBlock>

        <CollapseBlock title={t('Požiadavky na uchádzača')} collapsible={true}>
          <FormSelect
            label={t('Študijný program')}
            name="study_programs"
            placeholder={t('Vyhľadať študijné programy')}
            options={studyProgramsOptions}
            mode={'multiple'}
            rules={[required(t)]}
            onChange={handleStudyProgramsChange}
          />

          <FormTextArea label={t('IT zručnosti')} name="it_skills" />

          <FormTextArea
            label={t('Jazykové schopnosti')}
            name="language_knowledge"
          />

          <FormTextArea label={t('Iné požiadavky')} name="other_text" />
        </CollapseBlock>

        {/* Admin-only auto approve toggle at the end */}
        {isFijAdmin && (
          <CollapseBlock title={t('Schválenie')} collapsible={true}>
            <FormSwitch
              label={t('Automaticky schváliť')}
              name={'auto_approve'}
            />
          </CollapseBlock>
        )}

        <FormButtonsContainer>
          <FormButton submitOnClick={true} submitKey={'internalJobs'}>
            {archiveMode ? t('Vytvoriť FRI prax z archívu') : t('Uložiť')}
          </FormButton>
        </FormButtonsContainer>
      </Form>
    </>
  );
};

FijJobForm.propTypes = {
  areaOfUses: PropTypes.array,
  studyPrograms: PropTypes.array,
  teachers: PropTypes.array,
  initialValues: PropTypes.object,
  onSubmit: PropTypes.func,
  isCreate: PropTypes.bool,
  isTeacher: PropTypes.bool,
  archiveMode: PropTypes.bool,
  isFijAdmin: PropTypes.bool,
};

export default FijJobForm;
